import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { createStoredAuth } from "@am/shared";
import { ApiError, setCurrentKey, useApiClient } from "../api/client";

const keyStore = createStoredAuth("platform_admin_key", "platform_admin_remember");

interface AuthContextValue {
  status: "checking" | "anonymous" | "authenticated";
  tenantCount: number;
  login: (key: string, remember: boolean) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const api = useApiClient();
  const [status, setStatus] = useState<AuthContextValue["status"]>("checking");
  const [tenantCount, setTenantCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = keyStore.read();
      if (!stored) {
        if (!cancelled) setStatus("anonymous");
        return;
      }
      setCurrentKey(stored);
      try {
        const res = await api<{ ok: boolean; tenant_count: number }>("/admin/api/auth", { method: "POST" });
        if (cancelled) return;
        setTenantCount(res?.tenant_count ?? 0);
        setStatus("authenticated");
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) keyStore.clear();
        setCurrentKey(null);
        setStatus("anonymous");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(
    async (key: string, remember: boolean) => {
      setCurrentKey(key);
      try {
        const res = await api<{ ok: boolean; tenant_count: number }>("/admin/api/auth", { method: "POST" });
        keyStore.write(key, remember);
        setTenantCount(res?.tenant_count ?? 0);
        setStatus("authenticated");
      } catch (err) {
        setCurrentKey(null);
        throw err;
      }
    },
    [api],
  );

  const logout = useCallback(() => {
    keyStore.clear();
    setCurrentKey(null);
    setStatus("anonymous");
  }, []);

  const value = useMemo(() => ({ status, tenantCount, login, logout }), [status, tenantCount, login, logout]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
